import { createSlice } from '@reduxjs/toolkit';
// utils
import { axiosInstance as axios } from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  data: [],
  meta: null,
  record: null,
  messages: [],
  isSending: false,
};

const slice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isSending = false;
      state.error = action.payload;
    },

    // GET CONVERSATIONS
    getConversationsSuccess(state, action) {
      state.isLoading = false;
      state.data = action.payload.results;
      state.meta = action.payload.meta;
      state.error = false;
    },

    // GET CONVERSATION DETAIL
    getConversationSuccess(state, action) {
      state.isLoading = false;
      state.record = action.payload;
      state.messages = action.payload.messages || [];
      state.error = false;
    },

    // SEND OPERATOR MESSAGE
    startSending(state) {
      state.isSending = true;
    },

    sendMessageSuccess(state, action) {
      state.isSending = false;
      state.messages.push(action.payload);
      state.error = false;
      // Update record status
      if (state.record) {
        state.record.auto_reply_enabled = false;
        state.record.status = 'active';
      }
    },

    // TOGGLE AUTO REPLY
    toggleAutoReplySuccess(state, action) {
      state.isLoading = false;
      state.error = false;
      if (state.record) {
        state.record.auto_reply_enabled = action.payload.auto_reply_enabled;
        state.record.status = action.payload.status;
      }
    },

    // RESOLVE CONVERSATION
    resolveConversationSuccess(state, action) {
      state.isLoading = false;
      state.error = false;
      if (state.record) {
        state.record.status = action.payload.status;
      }
    },

    // POLL UPDATES
    pollUpdatesSuccess(state, action) {
      const { conversations } = action.payload;
      if (conversations && conversations.length > 0) {
        // Update existing conversation data with new unread counts
        conversations.forEach((updated) => {
          const idx = state.data.findIndex((c) => c.id === updated.id);
          if (idx !== -1) {
            state.data[idx] = { ...state.data[idx], ...updated };
          }
        });
      }
    },

    // POLL MESSAGES for active conversation
    pollMessagesSuccess(state, action) {
      const newMessages = action.payload.messages || [];
      if (newMessages.length > 0) {
        const existingIds = new Set(state.messages.map((m) => m.id));
        const toAdd = newMessages.filter((m) => !existingIds.has(m.id));
        state.messages.push(...toAdd);
      }
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getConversations(params = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/support/operator/conversations/', { params });
      dispatch(slice.actions.getConversationsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getConversation(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/support/operator/conversations/${id}/`);
      dispatch(slice.actions.getConversationSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function sendOperatorMessage(id, message, files = []) {
  return async () => {
    dispatch(slice.actions.startSending());
    try {
      let response;
      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('message', message || '');
        files.forEach((f) => formData.append('files', f));
        response = await axios.post(`/support/operator/conversations/${id}/messages/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await axios.post(`/support/operator/conversations/${id}/messages/`, { message });
      }
      dispatch(slice.actions.sendMessageSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function toggleAutoReply(id, enabled) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/support/operator/conversations/${id}/toggle-auto-reply/`, { enabled });
      dispatch(slice.actions.toggleAutoReplySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function resolveConversation(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/support/operator/conversations/${id}/resolve/`);
      dispatch(slice.actions.resolveConversationSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function pollUpdates(since) {
  return async () => {
    try {
      const params = since ? { since } : {};
      const response = await axios.get('/support/operator/poll/', { params });
      dispatch(slice.actions.pollUpdatesSuccess(response.data));
    } catch (error) {
      // Silent fail for polling
      console.error('Poll error:', error);
    }
  };
}

export function pollConversationMessages(id, since) {
  return async () => {
    try {
      const response = await axios.get(`/support/operator/conversations/${id}/`, {
        params: since ? { since } : {},
      });
      dispatch(slice.actions.pollMessagesSuccess({ messages: response.data.messages }));
    } catch (error) {
      console.error('Message poll error:', error);
    }
  };
}
